import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ColumnMappingService } from '@/services/columnMappingService'

const EMPTY_VALUE = '__none__'

interface DriverFieldMappingStepProps {
  headers: string[]
  identifierMapping: Record<string, string>
  profileFieldMapping: Record<string, string>
  onIdentifierMappingChange: (mapping: Record<string, string>) => void
  onProfileFieldMappingChange: (mapping: Record<string, string>) => void
}

const DRIVER_PLATFORM_KEY = 'driver_import'

export function DriverFieldMappingStep({
  headers,
  identifierMapping,
  profileFieldMapping,
  onIdentifierMappingChange,
  onProfileFieldMappingChange
}: DriverFieldMappingStepProps) {
  const config = useMemo(
    () => ColumnMappingService.getConfigForPlatform(DRIVER_PLATFORM_KEY),
    []
  )

  const identifierFields = Object.keys(config?.identifierFields ?? {})
  const profileFields = Object.keys(config?.profileFields ?? {})

  const requiredIdentifierFields = new Set(config?.requiredFields.identifierFields ?? [])
  const requiredProfileFields = new Set(config?.requiredFields.profileFields ?? [])

  const handleIdentifierChange = (field: string, value: string) => {
    const next = { ...identifierMapping }
    if (value === EMPTY_VALUE) {
      delete next[field]
    } else {
      next[field] = value
    }
    onIdentifierMappingChange(next)
  }

  const handleProfileChange = (field: string, value: string) => {
    const next = { ...profileFieldMapping }
    if (value === EMPTY_VALUE) {
      delete next[field]
    } else {
      next[field] = value
    }
    onProfileFieldMappingChange(next)
  }

  const renderFieldRow = (
    fieldKey: string,
    label: string,
    description: string,
    required: boolean,
    value: string | undefined,
    onChange: (field: string, value: string) => void
  ) => (
    <div
      key={fieldKey}
      className="flex flex-col gap-2 rounded-lg border p-4 bg-white"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-sm text-muted-foreground">{description}</div>
        </div>
        {required && <Badge variant="outline">Obligatorisk</Badge>}
      </div>
      <Select
        value={value ?? EMPTY_VALUE}
        onValueChange={(newValue) => onChange(fieldKey, newValue)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Välj kolumn" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={EMPTY_VALUE}>Ingen</SelectItem>
          {headers.map((header) => (
            <SelectItem key={header} value={header}>
              {header}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  // Check if any required identifier fields are missing
  const requiredMissing = useMemo(() => {
    const requiredFields = config?.requiredFields.identifierFields ?? []
    return requiredFields.some(field => !identifierMapping[field])
  }, [config, identifierMapping])

  // Generate dynamic required fields description
  const requiredFieldsDescription = useMemo(() => {
    const requiredFields = config?.requiredFields.identifierFields ?? []
    if (requiredFields.length === 0) return 'Inga obligatoriska fält'

    const fieldLabels = requiredFields.map(field => {
      const fieldConfig = getFieldDescription(field, 'identifier')
      return fieldConfig.label.toLowerCase()
    })

    if (fieldLabels.length === 1) return fieldLabels[0]
    if (fieldLabels.length === 2) return `${fieldLabels[0]} och ${fieldLabels[1]}`

    const last = fieldLabels[fieldLabels.length - 1]
    const rest = fieldLabels.slice(0, -1).join(', ')
    return `${rest} och ${last}`
  }, [config])

  return (
    <div className="space-y-6">
      <Alert>
        <AlertDescription>
          Mappa kolumner från filen till förarens fält. Följande fält krävs: {requiredFieldsDescription}.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Identifierare</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {identifierFields.map((fieldKey) => {
            const fieldConfig = getFieldDescription(fieldKey, 'identifier')
            return renderFieldRow(
              fieldKey,
              fieldConfig.label,
              fieldConfig.description,
              requiredIdentifierFields.has(fieldKey),
              identifierMapping[fieldKey],
              handleIdentifierChange
            )
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Profilfält</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {profileFields.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Inga profilfält definierade i konfigurationen.
            </div>
          ) : (
            profileFields.map((fieldKey) => {
              const fieldConfig = getFieldDescription(fieldKey, 'profile')
              return renderFieldRow(
                fieldKey,
                fieldConfig.label,
                fieldConfig.description,
                requiredProfileFields.has(fieldKey),
                profileFieldMapping[fieldKey],
                handleProfileChange
              )
            })
          )}
        </CardContent>
      </Card>

      {requiredMissing && (
        <Alert variant="destructive">
          <AlertDescription>
            För att fortsätta krävs: {requiredFieldsDescription}.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

function getFieldDescription(fieldKey: string, type: 'identifier' | 'profile') {
  if (type === 'identifier') {
    switch (fieldKey) {
      case 'driverName':
        return { label: 'Förarnamn', description: 'Fullständigt namn' }
      case 'driverId':
        return { label: 'Förar-ID', description: 'Unik identifierare eller UUID' }
      case 'email':
        return { label: 'E-post', description: 'Primär e-postadress' }
      case 'phone':
        return { label: 'Telefon', description: 'Primärt telefonnummer' }
      case 'personalCode':
        return { label: 'Personnummer', description: 'Personligt nummer' }
      case 'taxiCode':
        return { label: 'Taxikod', description: 'Taxibehörighetskod' }
      case 'employeeNumber':
        return { label: 'Anställningsnummer', description: 'Intern anställningskod' }
      default:
        return { label: fieldKey, description: '' }
    }
  }

  switch (fieldKey) {
    case 'bankAccount':
      return { label: 'Bankkonto', description: 'Bankkontonummer för utbetalningar' }
    case 'paymentReference':
      return { label: 'Betalningsreferens', description: 'Exempelvis OCR eller referens' }
    case 'taxTableName':
      return { label: 'Skattetabell', description: 'Individuell skattetabell' }
    case 'taxColumn':
      return { label: 'Skattekolumn', description: 'Kolumn i skattetabellen' }
    case 'salaryGarnishment':
      return { label: 'Löneutmätning', description: 'Belopp för löneutmätning' }
    default:
      return { label: fieldKey, description: '' }
  }
}
